// Story 1.36 D1 — GET/PUT /api/v1/account/wallet-details.
//
// The "one calm form" identity fields (legal name, date of birth,
// nationality, country of residence) that every money-capable endpoint
// gates on: until they are stored, top-up and the PSD2 refund answer 412
// `wallet_details_required` (Story 8.3 removed the payout and bank-account
// flows that used to be on this list). This hook pair
// backs the WalletDetailsPanel form on /account/wallet.
//
// GET returns each field nullable plus a `complete` flag (all five
// present). PUT is a FULL replacement — all five fields required — and
// echoes the stored state in the GET shape, so no follow-up read is
// needed. The server is the authoritative validator (underage DOB,
// malformed country codes, blank names); this hook surfaces its
// per-field errors verbatim.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type WalletDetails = {
  complete: boolean;
  first_name: string | null;
  last_name: string | null;
  /** ISO 8601 date (YYYY-MM-DD). */
  date_of_birth: string | null;
  /** ISO 3166-1 alpha-2. */
  nationality: string | null;
  /** ISO 3166-1 alpha-2. */
  residence_country: string | null;
};

export type WalletDetailsInput = {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  nationality: string;
  residence_country: string;
};

export type WalletDetailsError = {
  code: string;
  message: string;
  /** Set on 400 VALIDATION — names the offending field so the form can
   *  render the server's message under the right input. */
  field?: string;
};

export const WALLET_DETAILS_QUERY_KEY = ["wallet-details"] as const;

async function fetchWalletDetails(): Promise<WalletDetails> {
  const response = await fetch("/api/v1/account/wallet-details", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`wallet details fetch failed: ${response.status}`);
  }
  return (await response.json()) as WalletDetails;
}

export function useWalletDetails() {
  return useQuery({
    queryKey: WALLET_DETAILS_QUERY_KEY,
    queryFn: fetchWalletDetails,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}

async function putWalletDetails(
  input: WalletDetailsInput,
): Promise<WalletDetails> {
  const response = await fetch("/api/v1/account/wallet-details", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: WalletDetailsError = {
      code: "WALLET_DETAILS_FAILED",
      message: `Could not save your details (HTTP ${response.status})`,
    };
    try {
      const body = await response.json();
      if (body?.error) detail = body.error;
    } catch {
      // Body not JSON — keep the default error shape.
    }
    throw detail;
  }
  return (await response.json()) as WalletDetails;
}

export function useSaveWalletDetails() {
  const queryClient = useQueryClient();
  return useMutation<WalletDetails, WalletDetailsError, WalletDetailsInput>({
    mutationFn: putWalletDetails,
    onSuccess: (data) => {
      // Seed the query with the fresh state, then invalidate so any
      // in-flight consumers (the money flows' 412 gate) reconcile.
      queryClient.setQueryData(WALLET_DETAILS_QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: WALLET_DETAILS_QUERY_KEY });
    },
  });
}
